/* ==========================================================================
   Guide des tailles — comportement
   · déplace le lien à côté du libellé de l'option (« Taille »)
   · ouvre / ferme la modale (Échap, clic sur le fond, bouton de fermeture)
   · bascule cm / pouces
   · surligne la ligne correspondant à la taille sélectionnée
   ========================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('size-chart')) return;

  var STORAGE_KEY = 'size-chart:unit';
  var MAX_MOUNT_TRIES = 25;

  /* Sélecteurs testés dans l'ordre pour retrouver le libellé de l'option.
     Ajoutez-en un ici si votre thème utilise une autre structure. */
  var LABEL_SELECTORS = [
    'legend',
    '.variant-option__label',
    '.variant-option__name',
    '.product-form__label',
    '.form__label',
    'label'
  ];

  var openCount = 0;

  function normalize(value) {
    return (value == null ? '' : String(value))
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function containsWord(haystack, needle) {
    if (!haystack || !needle) return false;
    return (' ' + haystack + ' ').indexOf(' ' + needle + ' ') !== -1;
  }

  function toInches(raw) {
    return String(raw).replace(/\d+(?:[.,]\d+)?/g, function (match) {
      var value = parseFloat(match.replace(',', '.')) / 2.54;
      if (isNaN(value)) return match;
      return String(Math.round(value * 10) / 10).replace('.', ',');
    });
  }

  function readStoredUnit() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }
  }

  function writeStoredUnit(unit) {
    try {
      window.localStorage.setItem(STORAGE_KEY, unit);
    } catch (error) {
      /* navigation privée : on ignore */
    }
  }

  function lockScroll() {
    openCount += 1;
    document.documentElement.classList.add('size-chart-lock');
  }

  function unlockScroll() {
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0) document.documentElement.classList.remove('size-chart-lock');
  }

  class SizeChartElement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        /* L'élément a été déplacé dans le DOM : on se ré-accroche. */
        this.mountTries = 0;
        this.attemptMount();
        this.observe();
        return;
      }

      this.trigger = this.querySelector('[data-sc-trigger]');
      this.dialog = this.querySelector('[data-sc-dialog]');
      if (!this.trigger || !this.dialog) return;

      this.initialized = true;
      this.panel = this.querySelector('[data-sc-panel]');
      this.selectionNote = this.querySelector('[data-sc-selection]');
      this.anchor = null;
      this.scope = null;
      this.mountTries = 0;

      this.optionNames = (this.dataset.optionNames || '')
        .split(',')
        .map(normalize)
        .filter(Boolean);

      this.isOpen = false;

      this.handleTrigger = this.handleTrigger.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.cleanup = this.cleanup.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleScopeChange = this.handleScopeChange.bind(this);
      this.attemptMount = this.attemptMount.bind(this);
      this.handleMutation = this.handleMutation.bind(this);

      this.trigger.addEventListener('click', this.handleTrigger);
      this.dialog.addEventListener('click', this.handleDialogClick);
      /* Fermetures natives (Échap, geste du navigateur). Tous les moteurs
         n'émettent pas ces événements de façon fiable : `cleanup()` est
         idempotent et un contrôle différé sert de filet (voir handleKeydown). */
      this.dialog.addEventListener('close', this.cleanup);
      this.dialog.addEventListener('cancel', this.cleanup);
      this.dialog.addEventListener('keydown', this.handleKeydown);

      this.bindUnits();
      this.setUnit(readStoredUnit() === 'in' ? 'in' : 'cm', false);

      this.attemptMount();
      this.observe();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.clearTimeout(this.mountTimer);
      window.clearTimeout(this.remountTimer);
      if (this.isOpen) this.close();
    }

    /* -------------------------------------------------------- placement -- */

    attemptMount() {
      window.clearTimeout(this.mountTimer);
      if (this.mount()) return;

      this.mountTries += 1;
      if (this.mountTries > MAX_MOUNT_TRIES) {
        /* Libellé introuvable : le lien reste sous le sélecteur. */
        this.dataset.position = 'below';
        this.mount();
        return;
      }
      this.mountTimer = window.setTimeout(this.attemptMount, 120);
    }

    mount() {
      if (!this.anchor || !this.anchor.isConnected) this.anchor = this.findAnchor();

      if (this.anchor) {
        this.scope =
          this.anchor.closest('fieldset, .variant-option, .product-form__input') || this.anchor.parentElement;
        this.bindScope();
      }

      if (this.dataset.position !== 'label') {
        this.setAttribute('data-mounted', 'inline');
        return true;
      }

      if (!this.anchor) return false;

      var host = this.anchor;

      /* Un <label for="…"> renvoie les clics vers son champ : dans ce cas on
         place le lien à côté du libellé, dans un conteneur, pas dedans. */
      if (host.tagName === 'LABEL' && host.getAttribute('for')) {
        var wrapper = host.parentElement;
        if (!wrapper || !wrapper.classList.contains('size-chart-anchor')) {
          wrapper = document.createElement('div');
          wrapper.className = 'size-chart-anchor';
          host.parentNode.insertBefore(wrapper, host);
          wrapper.appendChild(host);
        }
        host = wrapper;
      } else {
        host.classList.add('size-chart-anchor');
      }

      /* Supprime un lien orphelin laissé par une instance précédente. */
      var stale = host.querySelectorAll('.size-chart__trigger');
      for (var i = 0; i < stale.length; i += 1) {
        if (stale[i] !== this.trigger) stale[i].remove();
      }

      if (this.trigger.parentElement !== host) host.appendChild(this.trigger);
      this.setAttribute('data-mounted', 'label');
      return true;
    }

    findAnchor() {
      if (!this.optionNames.length) return null;

      var scope = this.parentElement;
      for (var depth = 0; depth < 6 && scope; depth += 1) {
        var match = this.searchIn(scope);
        if (match) return match;
        scope = scope.parentElement;
      }
      return null;
    }

    searchIn(scope) {
      var names = this.optionNames;

      for (var i = 0; i < LABEL_SELECTORS.length; i += 1) {
        var candidates = scope.querySelectorAll(LABEL_SELECTORS[i]);

        for (var j = 0; j < candidates.length; j += 1) {
          var candidate = candidates[j];
          if (candidate.closest('size-chart')) continue;
          if (candidate.querySelector('input, select, textarea')) continue;

          var text = normalize(candidate.textContent);
          if (!text) continue;

          for (var k = 0; k < names.length; k += 1) {
            if (text === names[k] || containsWord(text, names[k])) return candidate;
          }
        }
      }
      return null;
    }

    /* Le sélecteur de variantes est ré-affiché à chaque changement de variante :
       si le lien disparaît du DOM, on le replace. */
    observe() {
      if (typeof window.MutationObserver !== 'function') return;
      if (this.observer) this.observer.disconnect();

      var root =
        this.closest('form, .product-details, .product-information, .shopify-section') || document.body;

      this.observer = new MutationObserver(this.handleMutation);
      this.observer.observe(root, { childList: true, subtree: true });
    }

    handleMutation() {
      if (this.dataset.position !== 'label') return;
      if (this.trigger.isConnected && this.trigger.closest('.size-chart-anchor')) return;

      window.clearTimeout(this.remountTimer);
      this.remountTimer = window.setTimeout(
        function () {
          this.anchor = null;
          this.mount();
        }.bind(this),
        60
      );
    }

    /* ----------------------------------------------------------- modale -- */

    handleTrigger(event) {
      event.preventDefault();
      event.stopPropagation();
      this.open();
    }

    open() {
      /* Filet : la modale a pu être fermée sans que l'événement natif arrive. */
      if (this.isOpen && !this.dialog.open) this.cleanup();
      if (this.isOpen) return;

      this.lastFocused = document.activeElement;
      this.highlightSelected();

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.isOpen = true;
      lockScroll();
      if (this.panel) this.panel.focus();
    }

    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      this.cleanup();
    }

    /* Idempotent : peut être appelé par `close()` et par l'événement natif. */
    cleanup() {
      if (!this.isOpen) return;
      this.isOpen = false;
      unlockScroll();

      /* On rend le focus au lien plutôt que de le laisser dans la modale fermée.
         Safari ne donne pas le focus aux boutons au clic : `lastFocused` peut
         donc être <body>, ce qui n'est pas focusable. */
      var target = this.lastFocused;
      if (!target || target === document.body || typeof target.focus !== 'function' || !target.isConnected) {
        target = this.trigger;
      }
      if (target && target.isConnected) target.focus();
      this.lastFocused = null;
    }

    handleDialogClick(event) {
      if (event.target.closest && event.target.closest('[data-sc-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      /* Un clic sur le fond a pour cible l'élément <dialog> lui-même. */
      if (event.target === this.dialog) this.close();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      if (typeof this.dialog.showModal !== 'function') {
        event.preventDefault();
        this.close();
        return;
      }

      /* Échap est traité nativement par <dialog> : on vérifie au tick suivant
         que le nettoyage a bien eu lieu, même sans événement `close`. */
      window.setTimeout(
        function () {
          if (!this.dialog.open) this.cleanup();
        }.bind(this),
        0
      );
    }

    /* ----------------------------------------------------------- unités -- */

    bindUnits() {
      var buttons = this.querySelectorAll('[data-sc-set-unit]');
      for (var i = 0; i < buttons.length; i += 1) {
        buttons[i].addEventListener(
          'click',
          function (event) {
            this.setUnit(event.currentTarget.getAttribute('data-sc-set-unit'), true);
          }.bind(this)
        );
      }
    }

    setUnit(unit, persist) {
      var target = unit === 'in' ? 'in' : 'cm';

      var cells = this.querySelectorAll('[data-cm]');
      for (var i = 0; i < cells.length; i += 1) {
        var raw = cells[i].getAttribute('data-cm') || '';
        cells[i].textContent = target === 'in' ? toInches(raw) : raw.replace(/\./g, ',');
      }

      var labels = this.querySelectorAll('[data-sc-unit]');
      for (var j = 0; j < labels.length; j += 1) {
        labels[j].textContent = target === 'in' ? 'po' : 'cm';
      }

      var buttons = this.querySelectorAll('[data-sc-set-unit]');
      for (var k = 0; k < buttons.length; k += 1) {
        buttons[k].setAttribute(
          'aria-pressed',
          buttons[k].getAttribute('data-sc-set-unit') === target ? 'true' : 'false'
        );
      }

      if (persist) writeStoredUnit(target);
    }

    /* --------------------------------------------- taille sélectionnée -- */

    bindScope() {
      if (!this.scope || this.scope === this.boundScope) return;
      if (this.boundScope) this.boundScope.removeEventListener('change', this.handleScopeChange);
      this.scope.addEventListener('change', this.handleScopeChange);
      this.boundScope = this.scope;
    }

    handleScopeChange() {
      if (this.isOpen) this.highlightSelected();
    }

    selectedValue() {
      var scope = this.scope;
      if (!scope) return '';

      var checked = scope.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (checked) {
        var value = (checked.value || '').trim();
        if (value) return value;

        if (checked.id) {
          try {
            var escaped = window.CSS && CSS.escape ? CSS.escape(checked.id) : checked.id;
            var label = scope.querySelector('label[for="' + escaped + '"]');
            if (label) return label.textContent.trim();
          } catch (error) {
            /* identifiant non échappable : on abandonne silencieusement */
          }
        }
        return '';
      }

      var select = scope.querySelector('select');
      if (select) return (select.value || '').trim();
      return '';
    }

    highlightSelected() {
      var raw = this.selectedValue();
      var value = normalize(raw);
      var rows = this.dialog.querySelectorAll('tbody tr');
      var matches = 0;

      for (var i = 0; i < rows.length; i += 1) {
        var head = rows[i].querySelector('th');
        var cell = head ? normalize(head.textContent) : '';
        var isMatch = Boolean(value) && (cell === value || containsWord(cell, value));

        rows[i].classList.toggle('is-current', isMatch);
        if (isMatch) {
          rows[i].setAttribute('aria-current', 'true');
          matches += 1;
        } else {
          rows[i].removeAttribute('aria-current');
        }
      }

      if (!this.selectionNote) return;

      if (matches) {
        this.selectionNote.textContent = '';
        this.selectionNote.appendChild(document.createTextNode('Votre sélection : '));
        var strong = document.createElement('strong');
        strong.textContent = raw;
        this.selectionNote.appendChild(strong);
        this.selectionNote.hidden = false;
      } else {
        this.selectionNote.hidden = true;
      }
    }
  }

  window.customElements.define('size-chart', SizeChartElement);
})();
